import { Link, useNavigate, useLocation } from "react-router-dom";
import { Facebook, Instagram, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, CONTACT, SOCIALS, NAV_LINKS, HOURS } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { Logo } from "@/components/site/Logo";

const ICONS = { facebook: Facebook, instagram: Instagram };

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (l) => {
    if (l.route) return navigate(l.route);
    if (!l.id) return;
    if (location.pathname !== "/") return navigate("/#" + l.id);
    scrollToId(l.id);
  };

  const facebookHref =
    SOCIALS.find((s) => s.key === "facebook")?.href || "#";

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0B132B] border-t border-white/10 pt-20 pb-10"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* Band Inquiry */}
        <div
          data-testid="band-inquiry"
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end pb-16 border-b border-white/10"
        >
          <div>
            <h3 className="font-display text-4xl md:text-5xl text-[#F8F5F2]">
              Got a band?{" "}
              <span className="text-primary">Play our stage.</span>
            </h3>
            <p className="mt-3 text-[#F8F5F2]/70 max-w-md">
              We book live music every Friday & Saturday. Reach out and let's
              get you on the calendar.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              data-testid="band-inquiry-facebook"
              href={facebookHref}
              target="_blank"
              rel="noreferrer"
              className="flex-1"
            >
              <Button className="w-full h-12 rounded-full font-label text-xs active:scale-95 transition-transform">
                <Facebook className="h-4 w-4" /> Message on Facebook
              </Button>
            </a>
            <a
              data-testid="band-inquiry-email"
              href={`mailto:${CONTACT.email}?subject=Band%20Inquiry%20%E2%80%94%20Alley%20Pub`}
              className="flex-1"
            >
              <Button
                variant="outline"
                className="w-full h-12 rounded-full font-label text-xs border-white/25 bg-white/5 text-[#F8F5F2] hover:bg-white/10 hover:text-primary active:scale-95 transition-transform"
              >
                <Mail className="h-4 w-4" /> Email Us
              </Button>
            </a>
          </div>
        </div>

        {/* Big wordmark + links */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 py-16">
          <div className="md:col-span-5">
            <Logo />
            <p className="mt-4 max-w-xs text-[#F8F5F2]/60 text-sm leading-relaxed">
              {BRAND.tagline}. Great drinks, great food, great fun in the heart
              of Bellevue.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => {
                const Icon = ICONS[s.key];
                return (
                  <a
                    key={s.key}
                    data-testid={`social-${s.key}`}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-[#F8F5F2] hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-label text-[11px] text-primary">Explore</h4>
            <ul className="mt-4 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => go(l)}
                    className="text-[#F8F5F2]/70 hover:text-primary transition-colors text-sm"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-label text-[11px] text-primary">Visit</h4>
            <address className="mt-4 not-italic text-sm text-[#F8F5F2]/70 space-y-1">
              <p>{CONTACT.address}</p>
              <p>{CONTACT.city}</p>
              <a
                href={CONTACT.phoneHref}
                className="block hover:text-primary transition-colors"
              >
                {CONTACT.phone}
              </a>
            </address>
            <ul className="mt-4 space-y-1">
              {HOURS.map((h) => (
                <li key={h.day} className="text-sm text-[#F8F5F2]/60">
                  <span className="text-[#F8F5F2]/80">{h.day}</span> — {h.time}
                </li>
              ))}
            </ul>
            <p
              data-testid="footer-age-notice"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-label text-[10px] text-primary tracking-[0.22em]"
            >
              21+ after 7 PM
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
          <p className="text-xs text-[#8E94A3]">
            © {new Date().getFullYear()} Alley Pub Nashville. All rights reserved.
          </p>
          <Link
            data-testid="footer-admin-link"
            to="/admin"
            className="text-xs text-[#8E94A3] hover:text-primary transition-colors font-label"
          >
            Staff Login
          </Link>
        </div>
      </div>
    </footer>
  );
}
